import React, {useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useGate, useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import * as yup from 'yup';

import {ReactComponent as DatamatrixIcon} from 'assets/svg/digital.svg';
import {LanguageSelector} from 'components/common/LanguageSelector';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {LABELS} from 'core/constants/labels';

import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {doLogout} from 'models/auth';
import {Gate, resetPasswordFx, state$} from 'models/password';

import {getPasswordSchema} from './helpers/passwordSchema';
import {PasswordValidationRules} from './helpers/passwordValidationRules';
import {
    ButtonContainer,
    CustomDatamatrixIcon,
    Field,
    Fields,
    Form,
    FormDescription,
    LanguageWrapper,
    MainContainer,
    MessageWrapper,
    PasswordContent,
    PasswordTitle,
    ServiceTitle,
} from './styled';

type ResetPasswordForm = {password: string; passwordConfirm: string};

export function ResetPasswordPage() {
    const {t, i18n} = useTranslation();
    const {search} = useLocation();
    useGate(Gate);

    const token = useMemo(() => new URLSearchParams(search).get('token'), [search]);
    const state = useStore(state$);

    const translateValidates = useCallback(
        (key: string, replace?: Record<string, any>) =>
            t(`pages.password.validations.${key}`, replace),
        [i18n.language],
    );

    const schema = useMemo(
        () =>
            yup.object().shape({
                password: getPasswordSchema(translateValidates),
                passwordConfirm: yup
                    .string()
                    .required(translateValidates('required'))
                    .oneOf([yup.ref('password')], translateValidates('confirm_password_mismatch')),
            }),
        [translateValidates],
    );

    const {control, handleSubmit, formState, watch, errors} = useForm<ResetPasswordForm>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: {
            password: '',
            passwordConfirm: '',
        },
    });

    const watchPassword = watch('password', '');

    const onLogin = () => history.push(ROUTE.LOGIN);

    useEffect(() => {
        doLogout(false);
    }, []);

    const onSubmit = async (data: ResetPasswordForm) => {
        if (!token) return;
        try {
            await resetPasswordFx({
                tokenType: 'RESET_PASSWORD',
                password: data.password,
                confirmPassword: data.passwordConfirm,
                token,
            });
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <>
            <div>
                <ServiceTitle size={4}>
                    <CustomDatamatrixIcon source={DatamatrixIcon} width={24} />
                    {LABELS.name}
                </ServiceTitle>
                <LanguageWrapper>
                    <LanguageSelector />
                </LanguageWrapper>
            </div>
            <MainContainer>
                <PasswordContent>
                    {!state.success && !state.expired && (
                        <>
                            <PasswordTitle size={3}>
                                {t('pages.password.reset.title')}
                            </PasswordTitle>
                            <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                                <FormDescription>
                                    {t('pages.password.reset.description')}
                                </FormDescription>
                                <Fields>
                                    <Field isError={!!errors.password}>
                                        <span>
                                            {t('pages.password.reset.fields.newPassword.label')}
                                        </span>
                                        <Controller
                                            name="password"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Input
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    type="password"
                                                    placeholder={t(
                                                        'pages.password.reset.fields.newPassword.placeholder',
                                                    )}
                                                    autoComplete="new-password"
                                                    disabled={state.pending}
                                                    hasError={!!errors.password}
                                                    isValid={
                                                        formState.dirtyFields.password &&
                                                        !errors.password
                                                    }
                                                />
                                            )}
                                        />
                                        <PasswordValidationRules watchValue={watchPassword} />
                                    </Field>

                                    <Field
                                        isError={!!errors.passwordConfirm}
                                        text={errors.passwordConfirm?.message}
                                    >
                                        <span>
                                            {t(
                                                'pages.password.reset.fields.passwordConfirmation.label',
                                            )}
                                        </span>
                                        <Controller
                                            name="passwordConfirm"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Input
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    type="password"
                                                    placeholder={t(
                                                        'pages.password.reset.fields.passwordConfirmation.placeholder',
                                                    )}
                                                    autoComplete="new-password"
                                                    disabled={state.pending}
                                                    hasError={!!errors.passwordConfirm}
                                                    isValid={
                                                        formState.dirtyFields.passwordConfirm &&
                                                        !errors.passwordConfirm
                                                    }
                                                />
                                            )}
                                        />
                                    </Field>
                                </Fields>

                                <ButtonContainer>
                                    <Button
                                        type="submit"
                                        isLoading={state.pending}
                                        disabled={!formState.isValid}
                                    >
                                        {t('pages.password.reset.submitButton')}
                                    </Button>
                                    <Button
                                        variant={ButtonVariant.Text}
                                        onClick={onLogin}
                                        isLoading={state.pending}
                                    >
                                        {t('pages.password.reset.returnButton')}
                                    </Button>
                                </ButtonContainer>
                            </Form>
                        </>
                    )}
                    {state.success && (
                        <MessageWrapper>
                            <PasswordTitle size={3}>
                                {t('pages.password.reset.success.title')}
                            </PasswordTitle>
                            <Button onClick={onLogin}>
                                {t('pages.password.reset.success.submit')}
                            </Button>
                        </MessageWrapper>
                    )}
                    {state.expired && (
                        <MessageWrapper>
                            <PasswordTitle size={3}>
                                {t('pages.password.set.expired.title')}
                            </PasswordTitle>
                        </MessageWrapper>
                    )}
                </PasswordContent>
            </MainContainer>
        </>
    );
}
